/**
 * Feedback Sync Middleware
 *
 * Automatically syncs new feedback events to Firestore when dispatched.
 * After each event, checks if the undigested count has reached the threshold
 * and triggers an AI taste profile digest if so.
 * Uses Redux Toolkit's createListenerMiddleware for clean side-effect handling.
 */

import { createListenerMiddleware } from '@reduxjs/toolkit';
import { addFeedbackEvent, setTasteProfile } from './feedbackSlice';
import { saveFeedbackEvent, saveTasteProfile } from '../services/feedbackSyncService';
import { isFirebaseEnabled } from '../services/firebaseConfig';
import { shouldTriggerDigest, digestFeedback } from '../services/tasteProfileService';
import type { RootState } from './store';

export const feedbackSyncMiddleware = createListenerMiddleware();

/** Prevent concurrent digest runs */
let digestInProgress = false;

// Sync new feedback events to Firestore + auto-trigger digest
feedbackSyncMiddleware.startListening({
  actionCreator: addFeedbackEvent,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const userId = state.auth?.user?.uid;

    if (!userId || !isFirebaseEnabled()) return;

    // 1. Sync the event to Firestore
    try {
      await saveFeedbackEvent(userId, action.payload);
    } catch (error) {
      console.warn('Failed to sync feedback event to Firestore:', error);
    }

    // 2. Check if we should trigger a taste profile digest
    if (digestInProgress) return;
    const { events, lastDigestedIndex } = (listenerApi.getState() as RootState).feedback;
    if (shouldTriggerDigest(events.length, lastDigestedIndex)) {
      digestInProgress = true;
      try {
        await digestFeedback(
          listenerApi.dispatch,
          listenerApi.getState,
          userId,
        );
      } catch (error) {
        console.warn('Auto-digest failed:', error);
      } finally {
        digestInProgress = false;
      }
    }
  },
});

// Sync taste profile updates to Firestore
feedbackSyncMiddleware.startListening({
  actionCreator: setTasteProfile,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState() as RootState;
    const userId = state.auth?.user?.uid;

    if (!userId || !isFirebaseEnabled()) return;

    try {
      await saveTasteProfile(userId, action.payload);
    } catch (error) {
      console.warn('Failed to sync taste profile to Firestore:', error);
    }
  },
});
